import { useState, useRef, useEffect, useContext } from "react";
import { useParams } from "react-router-dom";
import Swal from "sweetalert2";

import AuthContext from '../../components/js/modules/AuthContext';

import * as UserHandler from "../../components/js/modules/User";
import * as MessagingHandler from "../../components/js/modules/Messaging";
import * as TicketHandler from "../../components/js/modules/Tickets";
import * as AdminHandler from "../../components/js/modules/Admin";

import "../css/Tickets.css";
import "../css/TicketInfo.css";
import "../css/Topbar.css";

import TicketModerations from "../components/TicketModerations";
import TicketQuestionnaire from "../components/TicketQuestionnaire";
import TicketOverview from "../components/TicketOverview";
import TicketActions from "../components/TicketActions";
import TicketViewers from "../components/TicketViewers";
import Chat from "../components/Chat";

import Button from "../components/Button";
import TextBox from "../components/TextBox";
import Loading from "../components/Loading";
import Error from "../components/Error";
import { messagingSocket } from "../../components/js/modules/Socket";

const ticketsPerPage = 50;
const maxOpenTickets = 6;

const getTicketContributors = async (ticket) => {
    var contributors = {};
    for (var i = 0; i < ticket.messages.length; i++) {
        var message = ticket.messages[i];
        if (!contributors[message.user.account_id]) {
            contributors[message.user.account_id] = message.user;
        }
    }

    if (contributors["Bot"]) {
        delete contributors["Bot"];
    }
    return contributors;
}

const getTopicLogo = (ticket) => {
    for (var i = 0; i < ticket.panel.topics.length; i++) {
        var topic = ticket.panel.topics[i];
        if (topic.text == ticket.topic) {
            return topic.logo;
        }
    }
}

const getTimestamp = (unixTimestamp) => {
    const past = new Date(unixTimestamp * 1000);
    const now = new Date();

    const diffMs = now - past;
    const seconds = Math.max(0, Math.floor(diffMs / 1000));
    const minutes = Math.max(0, Math.floor(diffMs / (1000 * 60)));
    const hours = Math.max(0, Math.floor(diffMs / (1000 * 60 * 60)));
    const days = Math.max(0, Math.floor(diffMs / (1000 * 60 * 60 * 24)));

    if (seconds < 60) {
        return `${seconds} second${seconds !== 1 ? 's' : ''}`;
    } else if (minutes < 60) {
        return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
    } else if (hours < 24) {
        return `${hours} hour${hours !== 1 ? 's' : ''}`;
    } else {
        return `${days} day${days !== 1 ? 's' : ''}`;
    }
}

const determineData = async (ticketData, page, setData) => {
    var newData = {};
    var startIndex = (page * ticketsPerPage) - ticketsPerPage;
    var endIndex = startIndex + ticketsPerPage;

    for (var i = startIndex; i < endIndex; i++) {
        if (ticketData[i] != undefined) {
            newData[ticketData[i].ticket_id] = ticketData[i];
        }
    }
    
    if (setData) {
        setData(newData);
    } else {
        return newData;
    }
}

const TicketCard = ({selected, setTickets, ticketData}) => {
    const authContext = useContext(AuthContext);

    const topicLogo = getTopicLogo(ticketData);
    const timestamp = ticketData.messages[0] && getTimestamp(ticketData.messages[0].time) || getTimestamp(ticketData.ticket_created);

    const ticketProgress = ticketData.status == "Archived" && "Complete" || (ticketData.status == "Active" && (ticketData.staff_ids.length == 0 && "Awaiting Staff" || "Resolving"));
    const progressColor = (ticketProgress == "Complete" && "#34c940") || (ticketProgress == "Resolving" && "#ee8449") || (ticketProgress == "Awaiting Staff" && "#c93434")

    return <div 
        data-status={selected && "selected"} 
        className="ticket-card"
        onClick={() => setTickets((old) => {
            if (old && Object.hasOwn(old, ticketData.ticket_id)) {
                delete old[ticketData.ticket_id];
            } else {
                if (Object.keys(old).length >= maxOpenTickets) {
                    Swal.fire({text: `Failed to open ticket. You may only have ${maxOpenTickets} ongoing chats at a time.`, icon: 'info', confirmButtonText: 'Ok'});
                    return old;
                }

                ticketData.opened_view = Date.now();
                old[ticketData.ticket_id] = ticketData;
            }
            return Object.assign({}, old);
        })}
    >
        <div className="ticket-card-top-row">
            <i style={{"color": progressColor, fontSize: "0.8em"}} className="fa-solid fa-circle"></i>
            <span className="ticket-card-id">{ticketData.ticket_id.substring(0, 4)}</span>
            <img className="ticket-card-topic-img" src={topicLogo}/>
            <span className="ticket-card-title">{ticketData.topic}</span>

            {(() => {
                var count = 0;
                return Object.values(ticketData.contributors).map((user, _) => {
                    if (count > 1) return;
                    count++;

                    return <img title={user.rbxuser.username} className="ticket-card-contributor-img" src={user.rbxuser.imageurl}/>
                })
            })()}

            {(() => {
                const contributorCount = Math.max(Object.keys(ticketData.contributors).length - 2, 0);
                if (contributorCount > 0) {
                    return <span className="ticket-card-contributor-extras">{contributorCount}</span>
                }
            })()}
        </div>

        <div className="ticket-card-bottom-row">
            <img className="ticket-card-contributor-img" src={ticketData.account.rbxuser.imageurl}/>
            <span className="ticket-card-owner">{ticketData.account.rbxuser.username}<span className="ticket-card-id">&lt;{ticketData.account.id}&gt;</span></span>
            <span className="ticket-card-timestamp">{timestamp}</span>
        </div>
    </div>
}

const TicketsPagination = ({page, setPage, ticketData, setData}) => {
    const pages = Math.ceil(ticketData.length / ticketsPerPage);

    useEffect(() => {
        determineData(ticketData, page, setData)
    }, [page])

    const downPage = () => {
        if (page <= 1) return;

        setPage((old) => {
            return old - 1
        });
    }

    const upPage = () => {
        if (ticketData.length == 0 || page == pages) return;

        setPage((old) => {
            return old + 1
        });
    }

    return <div className = "ticket-pagination">
        <span className="ticket-pagination-status">Page {ticketData.length != 0 && page || "0"} of {pages}</span>
        <div className="ticket-pagination-btns">
            <Button
                onClick={downPage}
                data-status={page != 1 ? "enabled" : "disabled"}
                data-animation="raise"
                style={{padding: "10px 15px"}}
            >
                <i className="fa-solid fa-chevron-left fa-2x"></i>
            </Button>

            <Button
                onClick={upPage}
                data-status={pages != page? "enabled" : "disabled"}
                data-animation="raise"
            >
                <i className="fa-solid fa-chevron-right fa-2x"></i>
            </Button>
        </div>
    </div>
}

const TicketsSidebar = ({selectedTickets, setTickets, ticketData, pageData, setPageData, page, setPage}) => {
    return <div className="tickets-sidebar">
        {Object.keys(pageData).length == 0 && <h2>Nothing to see here...</h2>}

        <div className="tickets-list">
            {Object.values(pageData).map((ticket, _) => {
                return <TicketCard 
                    selected={selectedTickets && Object.hasOwn(selectedTickets, ticket.ticket_id)} 
                    setTickets={setTickets} 
                    ticketData={ticket}
                />
            })}
        </div>

        <TicketsPagination page={page} setPage={setPage} ticketData={ticketData} setData={setPageData}/>
    </div>
}

const TicketInfoSideview = ({active, hidingInfo, setHidingInfo, chatViewers, ticketData}) => {
    const authContext = useContext(AuthContext);

    const [page, setPage] = useState("info");

    return <div data-status={active && "show"} className="ticket-info">
        {active && <>
            <div className="ticket-info-header">
                <div style={{width: "100%"}} className="ticket-info-row-nowrap">
                    <img className="ticket-info-user-img" src={ticketData.account.rbxuser.imageurl}/>

                    <div style={{marginBottom: "5px", flex: "1 1 auto", minWidth: "0", overflow: "hidden"}} className="ticket-info-column">
                        <h3 className="ticket-info-user-name">{ticketData.account.rbxuser.username}</h3>
                        <span className="ticket-info-user-type"><i className="fa-solid fa-circle-user"></i>User</span>
                    </div>

                    <Button
                        onClick={() => {
                            setHidingInfo((old) => !old);
                        }}
                        data-animation="raise"
                    >
                        <i className={`fa-solid fa-chevron-${!hidingInfo ? "right" : "left"} fa-2x mobile-ticket-info`}></i>
                        <i className="fa-solid fa-ellipsis-vertical fa-2x ticket-info-btn"></i>
                    </Button>
                </div>

                <div className="ticket-info-row">
                    <span className="ticket-info-user-id">RBLX ID <i style={{margin: "0 5px"}}className="fa-solid fa-chevron-right"></i> {ticketData.account.rbxid} <i style={{margin: "0 5px"}} className="fa-solid fa-chevron-right"></i> Ticket {ticketData.ticket_id.substring(0, 4)}</span>
                </div>
            </div>

            <div data-type="icons" className="ticket-info-row">
                <Button 
                    data-status={page == "info" ? "selected" : "unselected"} 
                    data-type="basic"
                    title="Summary"
                    onClick={() => setPage("info")}
                >
                    <i class="fa-solid fa-circle-info fa-3x"></i>
                </Button>
                
                <Button 
                    data-status={page == "questionnaire" ? "selected" : "unselected"} 
                    data-type="basic"
                    title="Ticket Questionnaire"
                    onClick={() => setPage("questionnaire")}
                >
                    <i class="fa-solid fa-circle-question fa-3x"></i>
                </Button>

                <Button 
                    data-status={page == "moderation" ? "selected" : "unselected"} 
                    data-type="basic"
                    title="Moderation"
                    onClick={() => setPage("moderation")}
                >
                    <i class="fa-solid fa-shield-halved fa-3x"></i>
                </Button>
            </div>

            <div data-status={page == "questionnaire" && "show"} className="ticket-info-page">
                <TicketQuestionnaire
                    responses={ticketData.question_responses}
                />
            </div>

            <div data-status={page == "moderation" && "show"} className="ticket-info-page">
                <TicketModerations
                    ticketData={ticketData}
                />
            </div>

            <div data-status={page == "info" && "show"} className="ticket-info-page">
                <h3 className="ticket-info-page-alone-header">Summary</h3>

                <div className="ticket-info-column">
                    <h3>Contact Information</h3>
                    <div className="ticket-info-row">
                        <i class="fa-solid fa-envelope"></i>
                        <span>{authContext.user.permissionFlags.IS_ADMIN && ticketData.account.email || "[REDACTED]"}</span>
                    </div>
                    <div className="ticket-info-row">
                        <i class="fa-brands fa-discord"></i>
                        <span>{ticketData.account.discordid}</span>
                    </div>
                </div>

                <div style={{"gap": "10px"}} className="ticket-info-column">
                    <TicketOverview
                        ticketData={ticketData}
                    />
                </div>

                <div style={{"gap": "10px"}} className="ticket-info-column">
                    <TicketActions
                        ticketData={ticketData}
                    />
                </div>

                <div style={{"gap": "10px"}} className="ticket-info-column">
                    <TicketViewers
                        viewers={chatViewers}
                    />
                </div>
            </div>
        </>}
    </div>
}

const TopbarChat = ({ ticket, setTickets, active, setActiveTicket, chatStatus }) => {
    const topicLogo = getTopicLogo(ticket);

    return <div 
        data-status={active && "active"} 
        className="topbar-chat-option"
        onClick={() => setActiveTicket(ticket)}
    >
        <div className="topbar-chat-option-row">
            <img src={topicLogo}/>
            <span className="topbar-chat-option-topic">{ticket.topic}</span>
            <i 
                className="fa-solid fa-xmark"
                onClick={() => {
                    if (!active) return;

                    setTickets((old) => {
                        if (Object.hasOwn(old, ticket.ticket_id)) {
                            delete old[ticket.ticket_id];
                            return Object.assign({}, old);
                        }
                        return old;
                    })
                }}
            />
        </div>

        <div className="topbar-chat-option-row">
            {active && chatStatus == "loading" && <span style={{color: "#34c940"}} className="topbar-chat-option-user">Connecting...</span>}
            {(!active || chatStatus != "loading") && <span className="topbar-chat-option-user">{ticket.account.rbxuser.username} &lt;{ticket.ticket_id.substring(0, 4)}&gt;</span>}
        </div>
    </div>
}

const Topbar = ({ search, setSearch, setHidingSidebar, activeTicket, setActiveTicket, tickets, setTickets, chatStatus }) => {
    const authContext = useContext(AuthContext);

    const [profileMenu, setProfileMenu] = useState(false);

    return <div className="topbar">
        <div style={{"display": "none"}} className="topbar-mobile-btns">
            <i 
                onClick={() => setHidingSidebar((old) => {
                    return !old
                })}
                className="fa-solid fa-bars mobile-sidebar-btn fa-2x"
            />
        </div>

        <div className="topbar-view-options">            
            <TextBox 
                style={{width: "75%", fontSize: "1em", height: "52%"}} 
                placeholder="Search Tickets..."
                setState={setSearch}
            >
                {search}
            </TextBox>

            <div>
                <button>
                    <i className="fa-solid fa-table-list fa-2x"/>
                </button>

                <button>
                    <i className="fa-solid fa-table fa-2x"/>
                </button>
            </div>
        </div>

        <div className="topbar-chat">
            {Object.values(tickets).map((ticket, _) => {
                return <TopbarChat
                    ticket = {ticket}
                    setTickets = {setTickets}

                    chatStatus={chatStatus}

                    active = {activeTicket && activeTicket.ticket_id == ticket.ticket_id}
                    setActiveTicket = {setActiveTicket}
                />
            })}
        </div>
        
        <div className="topbar-actions-container">
            <div className="topbar-actions">
                <img 
                    src={authContext.user.rbxuser.imageurl}
                    onClick={() => {
                        setProfileMenu((old) => {
                            return !old;
                        });
                    }}
                />

                {profileMenu && <div className="topbar-menu">
                    <span><i className="fa-solid fa-user"></i> {authContext.user.email}</span>
                    <Button
                        data-status="unselected"
                        data-type="basic"
                        data-selectedcolor="lightgrey"

                        style={{borderRadius: "5px"}}

                        onClick={async () => {
                            await UserHandler.logout()
                            localStorage.clear();
                            window.location = "/.";
                        }}
                    >
                        <i className="fa-solid fa-right-from-bracket"></i> Logout
                    </Button>
                </div>}
            </div>
        </div>
    </div>
}

const Tickets = ({ ticketStatus }) => {
    const [status, setStatus] = useState("loading");
    const [hidingSidebar, setHidingSidebar] = useState(false);

    const [activeSearch, setActiveSearch] = useState(false);
    const [search, setSearch] = useState();
    const [page, setPage] = useState(1);

    const [tickets, setTickets] = useState({});
    const [activeTicket, setActiveTicket] = useState();
    const [sidebarPageData, setSidebarPageData] = useState({});
    const [hidingInfo, setHidingInfo] = useState(false);

    const [chatMessages, setChatMessages] = useState([]);
    const [chatStatus, setChatStatus] = useState("inactive");
    const [chatViewers, setChatViewers] = useState([]);

    const originalTicketData = useRef([]);
    const ticketData = useRef([]);
    const updatedTicketData = useRef();

    const { ticketId } = useParams();

    const replaceTicketData = (newData) => {
        if (!ticketData.current) return;

        for (var i = 0; i < ticketData.current.length; i++) {
            if (ticketData.current[i].ticket_id == newData.ticket_id) {
                ticketData.current[i] = newData;
                break;
            }
        }
    }

    const updateTicketData = async (newData, active) => {
        if (!newData.user_permissions) {
            newData.user_permissions = (await TicketHandler.getTicketPermissions(newData.ticket_id)).data;
        }
        newData.contributors = await getTicketContributors(newData);

        setTickets((old) => {
            newData.opened_view = Date.now();
            old[newData.ticket_id] = newData;

            return Object.assign({}, old);
        });

        replaceTicketData(newData);
        if (active) {
            updatedTicketData.current = newData;
        }

        setSidebarPageData((oldPageData) => {
            for (var i = 0; i < ticketData.current.length; i++) {
                if (Object.hasOwn(oldPageData, ticketData.current[i].ticket_id)) {
                    oldPageData[ticketData.current[i].ticket_id] = ticketData.current[i];
                }
            }
            return Object.assign({}, oldPageData);
        });
    }

    useEffect(() => {
        AdminHandler.getTickets(ticketStatus || "Active")
            .then(async (response) => {
                if (response.message == "Success") {
                    ticketData.current = response.data;
                    originalTicketData.current = response.data;

                    setSidebarPageData(await determineData(response.data, 1));

                    for (var i = 0; i < response.data.length; i++) {
                        var ticket = response.data[i];
                        if (ticketId == ticket.ticket_id && !activeTicket) {
                            setActiveTicket(ticket);
                        }
                        
                        ticket.contributors = await getTicketContributors(ticket);
                    }
                }
                setStatus("loaded");
            })
            .catch(() => {
                setStatus("error");
            });
    }, []);

    useEffect(() => {
        var newestTicket;
        for (const ticket of Object.values(tickets)) {
            if (!newestTicket || newestTicket.opened_view < ticket.opened_view) {
                newestTicket = ticket;
            }
        }
        setActiveTicket(newestTicket);
    }, [tickets]);

    useEffect(() => {
        if (!activeTicket) {
            updatedTicketData.current = undefined;
        }

        var newURL = "";
        if (ticketStatus == "Archived") {
            newURL = window.location.protocol + "//" + window.location.host + "/v2/staff/archive/tickets/" + (activeTicket && activeTicket.ticket_id || "");
        } else {
            newURL = window.location.protocol + "//" + window.location.host + "/v2/staff/tickets/" + (activeTicket && activeTicket.ticket_id || "");
        }
        var currentURL = window.location.protocol + '//' + window.location.host + window.location.pathname;

        if (newURL != currentURL) {
            window.history.pushState({path: newURL}, "", newURL);

            if (activeTicket) {
                setChatStatus("loading");

                MessagingHandler.joinChat(activeTicket.ticket_id, (res) => { 
                    if (res.status == "ok") {
                        updateTicketData(res.ticketInfo , true);

                        messagingSocket.off("ticketUpdated");
                        messagingSocket.off("ticketViewersUpdated");
                        messagingSocket.off("message");
                        messagingSocket.off("deleteMessage");

                        messagingSocket.on("ticketUpdated", (updater, newTicket, reason, forceReload) => {
                            if (forceReload) {
                                Swal.fire({
                                    title: "Updated",
                                    text: `This ticket has been updated: ${reason}`,
                                    icon: "success",
                                    confirmButtonText: "Ok"
                                }).then((result) => {
                                    window.location.reload();
                                });
                            } else {
                                updateTicketData(newTicket, true);
                            }
                        });

                        messagingSocket.on("ticketViewersUpdated", (activeUsers) => {
                            setChatViewers(activeUsers);
                        });

                        messagingSocket.on("message", (info) => {
                            if (info.ticket.ticket_id != activeTicket.ticket_id) return;
                            
                            setChatMessages((state) => {
                                const newMessages = [info.message, ...state];

                                info.ticket.messages = newMessages;
                                if (updatedTicketData.current) {
                                    updatedTicketData.current.messages = newMessages;
                                }
                                updateTicketData(info.ticket, true);

                                return newMessages;
                            });
                        });

                        messagingSocket.on('deleteMessage', (info) => {
                            if (info.ticket.ticket_id != activeTicket.ticket_id) return;

                            setChatMessages((state) => {
                                const message_index = state.findIndex((message) => {return message.message_id == info.message_id});
                                if (message_index >= 0) {
                                    state.splice(message_index, 1);

                                    info.ticket.messages = state;
                                    if (updatedTicketData.current) {
                                        updatedTicketData.current.messages = state;
                                    }
                                    updateTicketData(info.ticket, true);

                                    return [...state];
                                } else {
                                    return state;
                                }
                            })
                        });

                        setChatViewers(res.activeUsers);
                        setChatMessages(res.chatHistory);
                        setChatStatus("loaded");
                    } else {
                        setChatStatus("error");
                    }
                });
            }
        }
    }, [activeTicket]);

    useEffect(() => {
        if (status != "loaded") return;
        if (!search) {
            ticketData.current = originalTicketData.current;

            determineData(ticketData.current, 1, setSidebarPageData);
            setPage(1);
            
            return;
        }

        const includesMessageKeyword = (ticket) => {
            var result = false;
            
            for (var i = 0; i < ticket.messages.length; i++) {
                var message = ticket.messages[i];
                if (message.content.toLowerCase().includes(search.toLowerCase())) {
                    result = true;
                    break;
                }
            }

            return result;
        }

        const includesQuestionResponse = (ticket) => {
            var result = false;
            
            for (var i = 0; i < ticket.question_responses.length; i++) {
                var response = ticket.question_responses[i];
                if (response.value.toLowerCase().includes(search.toLowerCase())) {
                    result = true;
                    break;
                }
            }

            return result;
        }

        var results = originalTicketData.current.filter(ticket => {
            return ticket.topic.toLowerCase().includes(search.toLowerCase()) ||
                   ticket.account.rbxuser.username.toLowerCase().includes(search.toLowerCase()) ||
                   ticket.account.rbxid == search ||
                   ticket.ticket_id == search ||
                   (ticket.staff_ids[0] && ticket.staff_ids[0].rbxuser.username.toLowerCase().includes(search.toLowerCase())) ||
                   includesMessageKeyword(ticket) ||
                   includesQuestionResponse(ticket)
        });

        ticketData.current = results;
        determineData(results, 1, setSidebarPageData);
        setPage(1);
    }, [search]);

    return <>
        <Topbar 
            activeTicket = {activeTicket}
            setActiveTicket = {setActiveTicket}
            setHidingSidebar = {setHidingSidebar}

            search={search}
            setSearch={setSearch}

            chatStatus={chatStatus}

            tickets = {tickets}
            setTickets = {setTickets}
        />
        
        <div className="tickets-content">
            <Loading visible={status == "loading"} text="Loading Tickets"/>
            <Error visible={status == "error"} text="There was an issue loading tickets, contact the Network Engineer."></Error>

            {status == "loaded" && <>
                {!hidingSidebar && <TicketsSidebar 
                    selectedTickets={tickets} 
                    setTickets={setTickets} 
                    ticketData={ticketData.current} 

                    search={search}

                    page={page}
                    setPage={setPage}

                    pageData={sidebarPageData}
                    setPageData={setSidebarPageData}
                />}

                {!activeTicket && <div className="empty-page">
                    <span className="chats-empty">👻 It's quiet in here...</span>    
                </div>}

                {activeTicket && updatedTicketData.current && <Chat
                    status={chatStatus}

                    ticketData={updatedTicketData.current}
                    chatViewers={chatViewers}

                    hidingInfo={hidingInfo}
                    setHidingInfo={setHidingInfo}

                    messages={chatMessages}
                />}

                {updatedTicketData.current && activeTicket && chatStatus == "loaded" && !hidingInfo && <TicketInfoSideview
                    active={true}
                    ticketData={updatedTicketData.current}

                    hidingInfo={hidingInfo}
                    setHidingInfo={setHidingInfo}

                    chatViewers={chatViewers}
                />}
            </>}
        </div>
    </>
}

export default Tickets